import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Info, ScanLine, TriangleAlert } from 'lucide-react';
import { parseCellCode } from '@eombor/contracts';
import { Input, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { SuggestList, useSuggest, type SuggestOption } from '../../../shared/ui/Suggest.js';
import { warehouseApi, type CellDto } from '../../warehouse/api/warehouse.api.js';
import { CellCardDialog } from '../../warehouse/ui/CellCardDialog.js';
import { useWarehouseLayout } from '../../warehouse/model/warehouse.queries.js';

/** Skaner tugagach 250 ms — qo'lda yozayotgan odamga ham yetadi, harf sayin so'rov ketmaydi. */
export const CELL_LOOKUP_DEBOUNCE_MS = 250;

/** Namuna kod — format xatosi matnida ham, joy tutuvchida ham AYNAN shu ko'rinadi. */
export const CELL_CODE_SAMPLE = 'A-03-01-01';

/** Taklif ro'yxati shuncha qatordan oshmaydi: undan ko'pini odam ko'z bilan o'qimaydi. */
const MAX_SUGGESTIONS = 12;

/** Ro'yxatning eng kichik kengligi, piksel — 22rem. Kod bilan manzil birga sig'sin. */
const SUGGEST_MIN_WIDTH = 352;

export interface CellPickerProps {
  warehouseId: string;
  value: string;
  onChange: (code: string) => void;
  /** Tekshiruv natijasi: topilib, kirimga yaroqli bo'lsa katak; aks holda `null`. */
  onResolved: (cell: CellDto | null) => void;
  /** `Enter` — keyingi maydonga o'tish. Kod hali tekshirilmagan bo'lsa, natija kelgach chaqiriladi. */
  onEnter?: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  label?: string;
  hiddenLabel?: boolean;
  disabled?: boolean;
  /**
   * Tanlangan MAHSULOT. Berilsa, maydon bo'sh turganda shu mahsulot uchun
   * AJRATILGAN kataklar taklif qilinadi.
   *
   * NEGA KERAK: omborda tartib fizik — "bu bolt ana o'sha o'ringa
   * ketadi" degan qoida javonning o'zida yozuv bilan turadi. Skladchi
   * uni yoddan biladi, yangi kelgani esa bilmaydi va javonlar orasida
   * kod qidirib yuradi. Endi kod maydonining o'zi javob beradi.
   */
  nomenclatureId?: string | null;
  /**
   * Topilgan katakning manzilini SHU YERDA yozish. Kirim satrida `false`:
   * u yerda manzil qoldiq bilan birga, satr ostidagi keng qatorda chiqadi —
   * 13rem li ustunga "A zona · 03 stellaj · …" ham, qoldiq ham sig'maydi.
   */
  showPlace?: boolean;
  className?: string;
}

/** Kod qismlaridan odam o'qiydigan manzil: "A zona · 03 stellaj · 2-qator, 5-katak". */
export const cellPlaceText = (cell: {
  code: string;
  level: number;
  idx: number;
}): string | null => {
  const parts = parseCellCode(cell.code);
  if (parts === null) return null;
  return t('stock.cellPlace', {
    zone: parts.zoneCode,
    rack: parts.rackCode,
    level: cell.level,
    idx: cell.idx,
  });
};

/**
 * Katak kodini kiritish/skanerlash.
 *
 * SKANER OQIMI: skaner kodni bir zumda yozadi va oxirida `Enter` yuboradi.
 * Shu sababli `Enter` bosilganda javob hali kelmagan bo'lishi mumkin —
 * "kutilayotgan o'tish" eslab qolinadi va katak tasdiqlangan zahoti fokus
 * o'zi keyingi maydonga ketadi. Aks holda skladchi har satrda `Enter` ni ikki
 * marta bosishga majbur bo'lardi.
 *
 * QO'LDA YOZISH ham real yo'l: etiketka yirtiladi, skaner o'qimaydi. Shuning
 * uchun kod TO'LIQ formatga yetmaguncha (`parseCellCode` `null`) serverga
 * umuman borilmaydi — o'rniga shu ombordagi mos kataklar taklif qilinadi.
 * Bu ikki xabarni ham ajratadi: "format noto'g'ri" (yozuvni tuzat) va
 * "bunday katak yo'q" (boshqa katak izla) — omborchi uchun bular boshqa-boshqa
 * ish.
 *
 * `BLOCKED` katak ham XATO deb ko'rsatiladi: backend uni baribir rad etadi
 * (`Katak bloklangan — kirim qilib bo'lmaydi`), lekin odam buni miqdorni
 * kiritib, saqlagandan keyin emas, aynan shu yerda bilishi kerak.
 */
export const CellPicker = ({
  warehouseId,
  value,
  onChange,
  onResolved,
  onEnter,
  inputRef,
  label = t('stock.cellCode'),
  hiddenLabel = false,
  disabled = false,
  nomenclatureId = null,
  showPlace = true,
  className,
}: CellPickerProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [debounced, setDebounced] = useState('');
  const [focused, setFocused] = useState(false);
  /* `Esc` ro'yxatni yopadi, lekin fokus maydonda qoladi — shuning uchun
     yopilganlik alohida saqlanadi va keyingi harfda ro'yxat qaytadi. */
  const [dismissed, setDismissed] = useState(false);
  const pendingEnterRef = useRef(false);

  /* Chaqiruvlar refda: ular har chizishda yangi funksiya bo'ladi va effekt
     bog'liqligiga qo'shilsa, so'rov natijasi cheksiz aylanib qolardi. */
  const resolvedRef = useRef(onResolved);
  const enterRef = useRef(onEnter);
  useEffect(() => {
    resolvedRef.current = onResolved;
    enterRef.current = onEnter;
  });

  const normalized = value.trim().toUpperCase();
  const wellFormed = normalized !== '' && parseCellCode(normalized) !== null;
  const malformed = normalized !== '' && !wellFormed;

  useEffect(() => {
    const id = setTimeout(() => setDebounced(normalized), CELL_LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [normalized]);

  /* Serverga faqat TO'LIQ format bilan boriladi: chala kod baribir 404 beradi
     va o'sha 404 "bunday katak yo'q" bo'lib ko'rinib, odamni chalg'itardi. */
  const lookup = useQuery({
    queryKey: ['warehouse', 'cell-by-code', warehouseId, debounced],
    queryFn: () => warehouseApi.cellByCode(warehouseId, debounced),
    enabled: warehouseId !== '' && debounced !== '' && parseCellCode(debounced) !== null,
    retry: false,
    staleTime: 30_000,
  });

  /*
   * Ro'yxat FAQAT chala kod yozilganda ochiladi — etiketka yirtilgan va
   * kod qo'lda terilyapti degan holat.
   *
   * "Bu mahsulot qaysi o'ringa ketadi?" degan savolga javob bu yerda
   * EMAS: u satrning ostida, `ReservedCells` da ko'rinib turadi.
   * Ochiladigan ro'yxatga yashirilganda javob borligini faqat maydonga
   * bosgan odam bilardi.
   */
  const suggestOpen = focused && malformed && !dismissed;

  /* Takliflar ombor daraxtidan olinadi. Yangi endpoint yo'q: `layout` ham
     `by-code` bilan BIR XIL ruxsat va doirani talab qiladi (`warehouse.read`),
     ya'ni ro'yxatda odam ocholmaydigan katak paydo bo'lmaydi. */
  const [inspected, setInspected] = useState<string | null>(null);
  const layoutNeeded = (suggestOpen || inspected !== null) && warehouseId !== '';
  const layout = useWarehouseLayout(layoutNeeded ? warehouseId : null);

  interface Match {
    id: string;
    code: string;
    level: number;
    idx: number;
    blocked: boolean;
    /** Katak SHU mahsulot uchun ajratilgan. */
    reserved: boolean;
  }

  const matches = useMemo<Match[]>(() => {
    if (!suggestOpen) return [];

    const found: Match[] = [];
    for (const zone of layout.data?.zones ?? []) {
      for (const rack of zone.racks) {
        for (const cell of rack.cells) {
          if (!cell.code.startsWith(normalized)) continue;
          const reserved =
            nomenclatureId !== null &&
            cell.reservedFor.some((item) => item.id === nomenclatureId);
          found.push({
            id: cell.id,
            code: cell.code,
            level: cell.level,
            idx: cell.idx,
            blocked: cell.status === 'BLOCKED',
            reserved,
          });
        }
      }
    }

    /* Ajratilganlar TEPADA: ular "shu yerga qo'yilsin" degan qoida, qolgani
       esa shunchaki kodi o'xshagan kataklar. */
    return found
      .sort(
        (a, b) =>
          Number(b.reserved) - Number(a.reserved) || a.code.localeCompare(b.code),
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [suggestOpen, layout.data, normalized, nomenclatureId]);

  const options: SuggestOption[] = matches.map((cell) => {
    const place = cellPlaceText(cell);
    return {
      id: cell.id,
      label: cell.code,
      ...(place === null ? {} : { hint: place }),
      ...(cell.reserved ? { mark: t('stock.cellSuggestReserved') } : {}),
      ...(cell.blocked ? { tag: t('stock.cellSuggestBlocked') } : {}),
    };
  });

  const suggest = useSuggest(options.length, suggestOpen);


  const cell = lookup.data ?? null;
  const blocked = cell !== null && cell.status === 'BLOCKED';
  const usable = cell !== null && !blocked;
  const stale = normalized !== debounced;
  const checking = wellFormed && (stale ? true : lookup.isFetching);

  const message = malformed
    ? t('stock.cellCodeFormat', { sample: CELL_CODE_SAMPLE })
    : blocked
      ? t('stock.cellBlocked')
      : lookup.isError
        ? lookup.error instanceof ApiClientError
          ? lookup.error.message
          : t('stock.cellNotFound')
        : null;

  /* Natija o'zgarganda ota-sahifaga xabar beriladi va "kutilayotgan `Enter`"
     bajariladi. `stale` paytida `null` yuboriladi: eski katak yangi kod bilan
     saqlanib qolsa, satr noto'g'ri joyga yozilardi. */
  useEffect(() => {
    /* Format buzilgan bo'lsa ham `null`: aks holda kodning oxiriga ortiqcha
       belgi yozilganda satrda ESKI katakning `id` si qolib ketardi. */
    if (malformed || stale || checking) {
      resolvedRef.current(null);
      return;
    }
    resolvedRef.current(usable ? cell : null);
    if (usable && pendingEnterRef.current) {
      pendingEnterRef.current = false;
      enterRef.current?.();
    }
  }, [cell, usable, stale, checking, malformed]);

  const pickSuggestion = (index: number): void => {
    const match = matches[index];
    if (match === undefined) return;
    onChange(match.code);
    /* Tanlangan kod darhol tekshiriladi: odam `↓`+`Enter` bilan katakni
       tanlagach, yana 250 ms kutib o'tirmasin. */
    setDebounced(match.code);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // Taklif ro'yxati ochiq bo'lsa `↑`/`↓`/`Enter`/`Esc` unga tegishli.
    if (suggest.handleKeyDown(event, { onPick: pickSuggestion, onEscape: () => setDismissed(true) })) {
      return;
    }
    if (event.key !== 'Enter') return;
    // Forma yuborilmasin: `Enter` bu ekranda "keyingi maydon" degani.
    event.preventDefault();
    if (normalized === '' || malformed) return;
    if (usable && !stale) {
      enterRef.current?.();
      return;
    }
    // Javob hali yo'q — kutish oynasini darhol yopib, o'tishni navbatga qo'yamiz.
    pendingEnterRef.current = true;
    setDebounced(normalized);
  };

  const state: 'idle' | 'checking' | 'ok' | 'error' =
    normalized === ''
      ? 'idle'
      : malformed
        ? 'error'
        : checking
          ? 'checking'
          : usable
            ? 'ok'
            : message === null
              ? 'checking'
              : 'error';

  const statusText =
    state === 'ok' && cell !== null
      ? showPlace
        ? (cellPlaceText(cell) ?? cell.code)
        : ''
      : state === 'error'
        ? message
        : state === 'checking'
          ? t('stock.cellChecking')
          : '';

  return (
    <div ref={anchorRef} className={cn('flex min-w-0 flex-col gap-1', className)}>
      <Input
        ref={inputRef}
        mono
        // Yorliq yashirilganda ham nom qoladi: jadval boshi ustunni ko'z
        // uchun nomlaydi, ekran o'quvchi esa `aria-label` ni o'qiydi.
        {...(hiddenLabel ? { 'aria-label': label } : { label })}
        role="combobox"
        aria-expanded={suggestOpen}
        aria-controls={suggest.listId}
        aria-autocomplete="list"
        aria-activedescendant={
          suggestOpen && options.length > 0 ? suggest.optionId(suggest.active) : undefined
        }
        placeholder={CELL_CODE_SAMPLE}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setDismissed(false);
        }}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKeyDown}
        prefix={<ScanLine size={16} aria-hidden />}
        suffix={
          state === 'ok' ? (
            <Check size={16} aria-hidden className="text-accent" />
          ) : state === 'error' ? (
            <TriangleAlert size={16} aria-hidden className="text-danger" />
          ) : null
        }
      />

      {/* Holat matni jonli mintaqada: skaner bilan ishlayotgan odam ekranga
          qaramasa ham ekran o'quvchi natijani aytadi. Taklif ro'yxatidan
          YUQORIDA turadi — format xatosi maydonning aynan ostida ko'rinsin. */}
      <p
        aria-live="polite"
        className={cn(
          'min-h-4 truncate text-xs',
          state === 'ok' ? 'text-accent' : state === 'error' ? 'text-danger' : 'text-text-faint',
        )}
      >
        {statusText}
      </p>

      {suggestOpen ? (
        <SuggestList
          anchorRef={anchorRef}
          options={options}
          active={suggest.active}
          listId={suggest.listId}
          optionId={suggest.optionId}
          /* Ro'yxat ustundan KENGROQ: 13rem ga "A-01-01-01" bilan birga
             manzil sig'maydi va izoh kesilib ketardi. Suzayotgani uchun u
             qo'shni ustunlarni surib qo'ymaydi. */
          minWidth={SUGGEST_MIN_WIDTH}
          label={t('stock.cellSuggestLabel')}
          emptyText={t(layout.isPending ? 'stock.suggestLoading' : 'stock.cellSuggestEmpty')}
          onPick={pickSuggestion}
          onHover={suggest.setActive}
          /*
             Qatorning O'ZI tanlaydi — skaner bilan ishlayotgan odam uchun
             eng tez yo'l shu va u o'zgarmaydi. Belgi esa boshqa niyat:
             "tanlashdan oldin ko'rib olay, u yerda nima turibdi".
          */
          action={{
            icon: <Info size={14} aria-hidden />,
            label: (option) => t('stock.cellSuggestInspect', { code: option.label }),
            onAct: (index) => {
              const match = matches[index];
              if (match !== undefined) setInspected(match.id);
            },
          }}
        />
      ) : null}

      {/* Kartochka — turgan sahifadan chiqmasdan, faqat o'qish uchun. */}
      <CellCardDialog
        warehouseId={warehouseId}
        cellId={inspected}
        onClose={() => setInspected(null)}
        onUse={(code) => {
          onChange(code);
          setDebounced(code);
          setInspected(null);
        }}
      />

    </div>
  );
};
